import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lightbulb, LayoutGrid, TrendingUp } from 'lucide-react';
import { RiskMatrix } from './RiskMatrix';
import { SafetyObjectiveLens } from './SafetyObjectiveLens';
import { EnrichedRiskArea } from '../../types/prevention';
import { RiskAreaDetailModal } from '../RiskAreaDetailModal';
import { RiskMethodologyModal } from '../RiskMethodologyModal';
import { RecommendationsView } from './RecommendationsView';
import { AnalyticsView } from './AnalyticsView';
import { BiltiChatPanel } from './BiltiChatPanel';
import { useScreenInit } from '../../useScreenInit.js';
type PreventionSubTab = 'risk_matrix' | 'recommendations' | 'analytics';
export function PreventionView() {
  const screenInit = useScreenInit();
  const [subTab, setSubTab] = useState<PreventionSubTab>(screenInit.subTab as PreventionSubTab || 'risk_matrix');
  const [safetyObjective, setSafetyObjective] = useState<string>('all');
  const [selectedArea, setSelectedArea] = useState<EnrichedRiskArea | null>(null);
  const [showMethodology, setShowMethodology] = useState(false);
  const handleUpdateStatus = (_areaId: string, _newStatus: string, _notes: string, _userName: string) => {
    // Status update — in production this would persist the change
  };
  return <div className="h-[calc(100vh-110px)] min-h-[600px] -mt-2 flex flex-col" data-id="element-1765" data-mp="24v2e9y6ni5zk">
      {/* Sub-navigation bar */}
      <div className="flex items-center justify-between px-6 py-1.5 border-b border-gray-200 bg-white flex-shrink-0" data-id="element-1766" data-mp="2si0ucuquzbwy">
        <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-lg" data-id="element-1767" data-mp="2jjffpaoich1g">
          <button onClick={() => setSubTab('risk_matrix')} className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-all ${subTab === 'risk_matrix' ? 'bg-indigo-50 text-indigo-700 shadow-sm ring-1 ring-indigo-200' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`} data-id="element-1768" data-mp="2jj5kn8olvjow">
            <LayoutGrid size={15} className={subTab === 'risk_matrix' ? 'text-indigo-500' : 'text-gray-400'} data-id="element-1769" data-mp="3et27e2psr07w" />
            Risk Matrix
          </button>
          <button onClick={() => setSubTab('recommendations')} className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-all ${subTab === 'recommendations' ? 'bg-amber-50 text-amber-700 shadow-sm ring-1 ring-amber-200' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`} data-id="element-1770" data-mp="351o3liqtbe8y">
            <Lightbulb size={15} className={subTab === 'recommendations' ? 'text-amber-500' : 'text-gray-400'} data-id="element-1771" data-mp="2r5auoy0cgl63" />
            Insights & Corrective Actions
          </button>
          <button onClick={() => setSubTab('analytics')} className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-all ${subTab === 'analytics' ? 'bg-teal-50 text-teal-700 shadow-sm ring-1 ring-teal-200' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'}`} data-id="element-1772" data-mp="kbp6k4bb2pl4">
            <TrendingUp size={15} className={subTab === 'analytics' ? 'text-teal-500' : 'text-gray-400'} data-id="element-1773" data-mp="wmq27vvxq7ui" />
            Trends
          </button>
        </div>

        {/* Safety Performance Lens - hidden for now */}
        {/* <SafetyObjectiveLens
          value={safetyObjective}
          onChange={setSafetyObjective}
         /> */}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-hidden" data-id="element-1774" data-mp="dvgp7cmyh0y6">
        <AnimatePresence mode="wait" data-id="element-1775" data-mp="ivbexcuzvkhn">
          {subTab === 'risk_matrix' && <motion.div key="risk_matrix" initial={{
          opacity: 0,
          x: -10
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -10
        }} transition={{
          duration: 0.2
        }} className="h-full" data-id="element-1776" data-mp="it8gq5joul1u">
              <RiskMatrix onSelectArea={setSelectedArea} data-id="element-1777" data-mp="3c96533i6krks" />
            </motion.div>}
          {subTab === 'recommendations' && <motion.div key="recommendations" initial={{
          opacity: 0,
          x: 10
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: 10
        }} transition={{
          duration: 0.2
        }} className="h-full" data-id="element-1778" data-mp="37b4pwtnvlcel">
              <RecommendationsView data-id="element-1779" data-mp="8sfu0a7qroc8" />
            </motion.div>}
          {subTab === 'analytics' && <motion.div key="analytics" initial={{
          opacity: 0,
          x: 10
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: 10
        }} transition={{
          duration: 0.2
        }} className="h-full" data-id="element-1780" data-mp="3tmjmqnr4dga">
              <AnalyticsView data-id="element-1781" data-mp="4nvjm1ob9aqi" />
            </motion.div>}
        </AnimatePresence>
      </div>

      {/* Detail Modal — shared across all tabs */}
      <RiskAreaDetailModal riskArea={selectedArea} onClose={() => setSelectedArea(null)} onUpdateStatus={handleUpdateStatus} onOpenMethodology={() => setShowMethodology(true)} data-id="element-1782" data-mp="9fvg1k569lic" />
      {showMethodology && <RiskMethodologyModal onClose={() => setShowMethodology(false)} data-id="element-1783" data-mp="3hudt609yzr8t" />}

      {/* Floating Chat Panel */}
      <BiltiChatPanel tabContext={subTab} data-id="element-1784" data-mp="3lvyyn0k6d38z" />
    </div>;
}